import type { FAQItem } from './types';

export const securityFAQs: FAQItem[] = [
  {
    id: 'security-basics',
    question: 'How is our data protected?',
    answer: `Security fundamentals across tiers include:

• Encryption in transit and at rest
• Role-based access controls and permissions
• Regular security updates and monitoring
• Audit logs for sensitive actions

A formal backup and disaster recovery program is a design goal, not built or contracted today. Enterprise adds higher SLA and additional controls per agreement.`,
    category: 'security',
  },
  {
    id: 'data-residency',
    question: 'Where is our data stored? Do you support US-only data residency?',
    answer: `By default, platform data is stored in **United States regions** on infrastructure with standard provider-level redundancy. Enterprise deployments can request additional residency controls subject to infrastructure options. A dedicated backup product with a defined retention window is a design goal, not built today.

A secure, view-only document delivery mode (watermarked tiles, short-lived tokens, no-store headers, audit logging) is a design goal to reduce uncontrolled downloads. It exists today only as a client-side mock with no backend rendering or token issuance; it is not a live feature.`,
    category: 'security',
  },
  {
    id: 'pii-and-access-controls',
    question: 'How do you protect PII and restrict access?',
    answer: `We apply multiple controls across tiers:

• **Encryption** in transit (TLS) and at rest
• **Role-based access control** (residents, board, admins, vendors)
• **Audit logs** for sensitive actions (votes, finance, secure docs)
• **Step-up authentication** before viewing sensitive documents (optional)
• **Least-privilege defaults** and documented permission models

Enterprise adds higher SLA and advanced controls per agreement.`,
    category: 'security',
  },
  {
    id: 'secure-docs-spec',
    question: 'Will residents be able to view sensitive PDFs without downloading them?',
    answer: `This is a design goal, not a live feature. The intended **Secure Document Delivery** experience would render PDFs as **watermarked tiles**:

• Short-lived tokens scoped to (user, tenant, document)
• No-store headers and strict CSP
• Watermark every tile (user/email/time/document)
• Optional redaction for sensitive fields
• Full audit trail of views

Today this exists only as a client-side mock that serves placeholder images; there is no backend token issuance, rendering, or watermarking. Ask us for current build status before relying on it for sensitive documents.`,
    category: 'security',
  },
  {
    id: 'uptime-sla',
    question: 'What uptime do you guarantee?',
    answer: `We use commercially reasonable efforts to maintain platform availability using industry-leading infrastructure (AWS, Google Cloud, Cloudflare CDN).

**Important:** We do NOT guarantee a specific uptime percentage (e.g., "99.5% SLA") due to dependencies on third-party infrastructure beyond our control, including:
• Cloud provider regional outages
• DDoS attacks beyond our mitigation capacity
• Internet backbone failures
• Power grid disruptions affecting data centers

**Optional High-Availability Add-On:**
Communities requiring enhanced uptime can purchase multi-region redundancy (targets 99.9% availability) at additional cost. Contact sales for pricing.

A formal disaster recovery program with automated backups and a defined retention window is a design goal, not built or contracted today.`,
    category: 'security',
  },
  {
    id: 'data-ownership',
    question: 'Who owns our data and what happens if we terminate?',
    answer: `Per the service agreement, your community owns all Client Data.

Upon termination:
• We provide a standard data export
• Your data is securely deleted within 90 days
• Confidential information is returned or destroyed

There is no separate backup archive today, so there is nothing further to purge on a delayed schedule; a formal backup retention process is a design goal, not built yet.

This ensures your community retains control of its records at all times.`,
    category: 'security',
  },
];
